/* dgs-floral-designer.js
 * ----------------------
 * Floral basket disc designer for the (formerly Valentine's) floral disc.
 *
 * Same architecture as dgs-wedding-designer.js: the owner's basket-and-roses
 * artwork is drawn as the canvas background, the buyer's two optional text
 * lines render beneath the basket, and at add-to-cart the design is rendered
 * at 2400px (8" @ 300 DPI) to a JPEG attached as properties[_DGS Design URL]
 * via a hidden file input — cart-drawer.js posts it multipart, Shopify stores
 * it on its CDN, and the print-batch tool files it under the chosen mold.
 *
 * Both text lines are optional ON PURPOSE: the artwork stands alone, and the
 * print file is always generated, so a blank design is unambiguous — no
 * "did they forget?" checkbox needed (unlike the old textarea flow).
 */
(function () {
  'use strict';

  var root = document.getElementById('dgs-fl-designer');
  if (!root) return;

  var form = root.closest('form');
  if (!form) return;

  var els = {
    line1: document.getElementById('dgs-fl-line1'),
    line2: document.getElementById('dgs-fl-line2'),
    canvas: document.getElementById('dgs-fl-preview'),
    file: document.getElementById('dgs-fl-file'),
    status: document.getElementById('dgs-fl-status')
  };

  var ART = 2400;

  // ---------- artwork ----------
  var artImg = null, artState = 'idle';
  var artWaiters = [];
  function artReady() {
    return new Promise(function (resolve) {
      if (artState === 'ready' || artState === 'failed') { resolve(); return; }
      artWaiters.push(resolve);
      if (artState === 'loading') return;
      artState = 'loading';
      var url = root.getAttribute('data-art-url');
      if (!url) { artState = 'failed'; artWaiters.splice(0).forEach(function (r) { r(); }); return; }
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        artImg = img;
        artState = 'ready';
        artWaiters.splice(0).forEach(function (r) { r(); });
      };
      img.onerror = function () {
        artState = 'failed';
        artWaiters.splice(0).forEach(function (r) { r(); });
      };
      img.src = url;
    });
  }

  // ---------- fonts ----------
  var fontsPromise = null;
  function fontsReady() {
    if (!fontsPromise) {
      fontsPromise = Promise.all([
        document.fonts.load('400 100px "Great Vibes"'),
        document.fonts.load('600 100px "Cormorant Garamond"')
      ]).catch(function () { /* draw with fallbacks rather than never drawing */ });
    }
    return fontsPromise;
  }

  function designState() {
    return {
      line1: (els.line1.value || '').trim(),
      line2: (els.line2.value || '').trim()
    };
  }

  // ---------- drawing ----------
  var COLORS = {
    disc: '#FDFCFA',
    line1: '#7a2433',  // deep rose, matching the artwork's blooms
    line2: '#57684e'   // muted sage, matching the ivy
  };
  var SCRIPT = '"Great Vibes", "Snell Roundhand", cursive';
  var SERIF = '"Cormorant Garamond", Georgia, serif';

  function drawSpaced(ctx, text, cx, baseline, spacing) {
    var widths = [], total = 0, i;
    for (i = 0; i < text.length; i++) {
      var w = ctx.measureText(text[i]).width;
      widths.push(w);
      total += w;
    }
    total += spacing * (text.length - 1);
    var x = cx - total / 2;
    var align = ctx.textAlign;
    ctx.textAlign = 'left';
    for (i = 0; i < text.length; i++) {
      ctx.fillText(text[i], x, baseline);
      x += widths[i] + spacing;
    }
    ctx.textAlign = align;
  }

  function fitSize(ctx, text, baseSize, weight, family, spacingEm, maxWidth) {
    var size = baseSize;
    for (var guard = 0; guard < 8; guard++) {
      ctx.font = weight + ' ' + size + 'px ' + family;
      var w = 0;
      for (var i = 0; i < text.length; i++) w += ctx.measureText(text[i]).width;
      w += spacingEm * size * (text.length - 1);
      if (w <= maxWidth) break;
      size = Math.floor(size * (maxWidth / w) * 0.98);
    }
    return size;
  }

  function drawDesign(ctx, size, st, pageBg) {
    var s = size / ART;
    ctx.save();
    ctx.scale(s, s);
    ctx.fillStyle = pageBg || '#FFFFFF';
    ctx.fillRect(0, 0, ART, ART);
    if (artImg) {
      // owner-supplied square artwork with its own white background
      ctx.drawImage(artImg, 0, 0, ART, ART);
    } else {
      ctx.beginPath();
      ctx.arc(1200, 1200, 1188, 0, Math.PI * 2);
      ctx.fillStyle = COLORS.disc;
      ctx.fill();
    }

    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    // text sits in the clear zone beneath the basket art; ink-centered
    // because script capitals carry big left swashes
    var TEXT_CX = 1200;
    function inkCenteredX(text) {
      var m = ctx.measureText(text);
      if (m.actualBoundingBoxLeft === undefined) return TEXT_CX;
      return TEXT_CX - (m.actualBoundingBoxRight - m.actualBoundingBoxLeft) / 2;
    }

    var both = st.line1 && st.line2;
    if (st.line1) {
      var fs1 = fitSize(ctx, st.line1, 170, '400', SCRIPT, 0, 1000);
      ctx.font = '400 ' + fs1 + 'px ' + SCRIPT;
      ctx.fillStyle = COLORS.line1;
      ctx.fillText(st.line1, inkCenteredX(st.line1), both ? 2085 : 2140);
    }
    if (st.line2) {
      var fs2 = fitSize(ctx, st.line2, 68, '600', SERIF, 0.22, 760);
      ctx.font = '600 ' + fs2 + 'px ' + SERIF;
      ctx.fillStyle = COLORS.line2;
      drawSpaced(ctx, st.line2.toUpperCase(), TEXT_CX, both ? 2215 : 2160, 0.22 * fs2);
    }

    ctx.restore();
  }

  // ---------- preview ----------
  var previewTimer = null;
  function renderPreview() {
    var cssSize = els.canvas.clientWidth || 320;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var px = Math.round(cssSize * dpr);
    if (els.canvas.width !== px) { els.canvas.width = px; els.canvas.height = px; }
    var ctx = els.canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, px, px);
    drawDesign(ctx, px, designState(), 'rgba(0,0,0,0)');
  }
  function schedulePreview() {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(function () {
      Promise.all([fontsReady(), artReady()]).then(renderPreview);
    }, 120);
  }

  // ---------- print file ----------
  var PRINT_SIZE = 2400;
  var JPEG_QUALITY = 0.92;
  var printTimer = null;
  var printSeq = 0;

  function attachDisabled(disabled) {
    els.file.disabled = disabled;
  }

  function buildPrintFile() {
    var seq = ++printSeq;
    var st = designState();
    return Promise.all([fontsReady(), artReady()]).then(function () {
      if (seq !== printSeq) return false;
      var c = document.createElement('canvas');
      c.width = PRINT_SIZE;
      c.height = PRINT_SIZE;
      drawDesign(c.getContext('2d'), PRINT_SIZE, st, '#FFFFFF');
      return new Promise(function (resolve) {
        c.toBlob(function (blob) {
          if (!blob || seq !== printSeq) { resolve(false); return; }
          try {
            var slug = (st.line1 + '-' + st.line2).toLowerCase()
              .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'no-text';
            var dt = new DataTransfer();
            dt.items.add(new File([blob], 'floral-' + slug + '.jpg', { type: 'image/jpeg' }));
            els.file.files = dt.files;
            attachDisabled(false);
            resolve(true);
          } catch (err) {
            attachDisabled(true);
            resolve(false);
          }
        }, 'image/jpeg', JPEG_QUALITY);
      });
    });
  }

  function schedulePrintFile() {
    attachDisabled(true);
    clearTimeout(printTimer);
    printTimer = setTimeout(function () {
      printTimer = null; // submit fast-path reads this as "attached file is fresh"
      buildPrintFile();
    }, 900);
  }

  // ---------- form wiring ----------
  function onFieldChange() {
    schedulePreview();
    schedulePrintFile();
  }
  ['input', 'change'].forEach(function (evt) {
    [els.line1, els.line2].forEach(function (el) {
      el.addEventListener(evt, onFieldChange);
    });
  });

  var passThrough = false;
  form.addEventListener('submit', function (e) {
    if (passThrough) { passThrough = false; return; }
    if (!els.file.disabled && els.file.files.length && !printTimer) return;
    e.preventDefault();
    e.stopPropagation();
    clearTimeout(printTimer);
    printTimer = null;
    if (els.status) els.status.textContent = 'Preparing your print file…';
    buildPrintFile().then(function () {
      if (els.status) els.status.textContent = '';
      passThrough = true;
      form.requestSubmit();
    });
  });

  // ---------- boot ----------
  attachDisabled(true);
  // this product's design is valid with NO text, so attach the plain-art
  // print file up front; typing regenerates it
  if ('requestIdleCallback' in window) {
    requestIdleCallback(function () { Promise.all([fontsReady(), artReady()]).then(function () { renderPreview(); buildPrintFile(); }); });
  } else {
    setTimeout(function () { Promise.all([fontsReady(), artReady()]).then(function () { renderPreview(); buildPrintFile(); }); }, 400);
  }
  window.addEventListener('resize', schedulePreview);
})();
